import { useEffect, useState } from 'react';
import { Save, Plus, Trash2, ExternalLink, Banknote, Handshake, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button, Card, CardHeader, CardTitle, CardContent, Input } from '@nepal-ecom/ui';
import { PageHeader } from '@/components/shared/PageHeader';
import { STOREFRONT_URL, cmsUrl, cmsFetch } from '@/lib/cms';

const CMS_URL = cmsUrl('partners');

interface FinancialPartner {
  _id: string;
  companyName: string;
  personName: string;
  socialLinks: string[];
}

interface ManagingPartner {
  _id: string;
  companyName: string;
  persons: string[];
}

interface PartnersContent {
  financialPartners: FinancialPartner[];
  managingPartners: ManagingPartner[];
}

const uid = () => Math.random().toString(36).slice(2);
const EMPTY_FP = (): FinancialPartner => ({ _id: uid(), companyName: '', personName: '', socialLinks: [] });
const EMPTY_MP = (): ManagingPartner => ({ _id: uid(), companyName: '', persons: [''] });

const DEFAULT_CONTENT: PartnersContent = { financialPartners: [], managingPartners: [] };

function FinancialRow({
  partner, index, onChange, onDelete,
}: {
  partner: FinancialPartner;
  index: number;
  onChange: (i: number, p: FinancialPartner) => void;
  onDelete: (i: number) => void;
}) {
  const set = (field: keyof FinancialPartner, val: string | string[]) =>
    onChange(index, { ...partner, [field]: val });

  const addLink = () => set('socialLinks', [...partner.socialLinks, '']);
  const updateLink = (j: number, val: string) => {
    const links = [...partner.socialLinks];
    links[j] = val;
    set('socialLinks', links);
  };
  const removeLink = (j: number) =>
    set('socialLinks', partner.socialLinks.filter((_, idx) => idx !== j));

  return (
    <div className="p-4 rounded-xl border border-slate-200 bg-white hover:border-slate-300 transition-colors space-y-3">
      <div className="flex items-start gap-3">
        <span className="text-sm font-bold tabular-nums text-slate-400 mt-2 w-5 flex-shrink-0">{index + 1}.</span>
        <div className="grid sm:grid-cols-2 gap-3 flex-1 min-w-0">
          <label className="block">
            <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Company / Product Name</span>
            <Input value={partner.companyName} onChange={(e) => set('companyName', e.target.value)}
              placeholder="Business Oxygen" className="h-8 mt-1 text-sm" />
          </label>
          <label className="block">
            <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Contact Person</span>
            <Input value={partner.personName} onChange={(e) => set('personName', e.target.value)}
              placeholder="Ram Shrestha" className="h-8 mt-1 text-sm" />
          </label>
        </div>
        <button type="button" onClick={() => onDelete(index)}
          className="mt-2 p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
          aria-label="Remove">
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      <div className="ml-8 space-y-1.5">
        <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block">Social / Website Links</span>
        {partner.socialLinks.map((link, j) => (
          <div key={j} className="flex items-center gap-2">
            <Input value={link} onChange={(e) => updateLink(j, e.target.value)}
              placeholder="https://linkedin.com/..." className="h-8 text-sm font-mono flex-1" />
            <button type="button" onClick={() => removeLink(j)}
              className="p-1 text-slate-400 hover:text-red-500 rounded transition-colors flex-shrink-0">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={addLink} className="h-7 text-xs">
          <Plus className="h-3 w-3" /> Add link
        </Button>
      </div>
    </div>
  );
}

function ManagingRow({
  partner, index, onChange, onDelete,
}: {
  partner: ManagingPartner;
  index: number;
  onChange: (i: number, p: ManagingPartner) => void;
  onDelete: (i: number) => void;
}) {
  const set = (field: keyof ManagingPartner, val: string | string[]) =>
    onChange(index, { ...partner, [field]: val });

  const updatePerson = (j: number, val: string) => {
    const persons = [...partner.persons];
    persons[j] = val;
    set('persons', persons);
  };
  const addPerson = () => set('persons', [...partner.persons, '']);
  const removePerson = (j: number) =>
    set('persons', partner.persons.filter((_, idx) => idx !== j));

  return (
    <div className="flex items-start gap-3 p-4 rounded-xl border border-slate-200 bg-white hover:border-slate-300 transition-colors">
      <span className="text-sm font-bold tabular-nums text-slate-400 mt-2 w-5 flex-shrink-0">{index + 1}.</span>
      <div className="flex-1 min-w-0 space-y-2">
        <label className="block">
          <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Company / Product Name</span>
          <Input value={partner.companyName} onChange={(e) => set('companyName', e.target.value)}
            placeholder="Khalti" className="h-8 mt-1 text-sm" />
        </label>
        <div>
          <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block mb-1">Person Names</span>
          <div className="space-y-1.5">
            {partner.persons.map((p, j) => (
              <div key={j} className="flex items-center gap-2">
                <Input value={p} onChange={(e) => updatePerson(j, e.target.value)}
                  placeholder="Person Name" className="h-8 text-sm flex-1" />
                {partner.persons.length > 1 && (
                  <button type="button" onClick={() => removePerson(j)}
                    className="p-1 text-slate-400 hover:text-red-500 rounded transition-colors flex-shrink-0">
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addPerson} className="h-7 text-xs">
              <Plus className="h-3 w-3" /> Add person
            </Button>
          </div>
        </div>
      </div>
      <button type="button" onClick={() => onDelete(index)}
        className="mt-2 p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
        aria-label="Remove">
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
}

export function PartnersCms() {
  const [data, setData] = useState<PartnersContent>(DEFAULT_CONTENT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await cmsFetch(CMS_URL, { cache: 'no-store' });
        if (res.ok) {
          const json = await res.json();
          const hydrate = <T extends { _id?: string }>(arr: T[]): (T & { _id: string })[] =>
            (arr ?? []).map((x) => ({ ...x, _id: x._id ?? uid() }));
          setData({
            financialPartners: hydrate(json.financialPartners),
            managingPartners: hydrate(json.managingPartners),
          });
        }
      } catch {
        toast.error('Could not reach storefront CMS.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const setFP = (list: FinancialPartner[]) => setData((d) => ({ ...d, financialPartners: list }));
  const setMP = (list: ManagingPartner[]) => setData((d) => ({ ...d, managingPartners: list }));

  const handleSave = async () => {
    const hasBlank = [...data.financialPartners, ...data.managingPartners].some((p) => !p.companyName.trim());
    if (hasBlank) { toast.error('All partners need a company name.'); return; }
    setSaving(true);
    try {
      const res = await cmsFetch(CMS_URL, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success('Partners saved', { description: 'Refresh the storefront to see changes.' });
    } catch (err) {
      toast.error('Save failed', { description: (err as Error).message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="animate-fade-in space-y-5">
      <PageHeader
        title="Partners & Backers"
        description="Manage financial partners (numbered list) and managing partners (table) shown on the About Us page."
        actions={
          <div className="flex items-center gap-2">
            <Button asChild variant="outline" size="sm">
              <a href={`${STOREFRONT_URL}/about`} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4" /> Open About page
              </a>
            </Button>
            <Button onClick={handleSave} disabled={saving || loading}>
              <Save className="h-4 w-4" /> {saving ? 'Saving…' : 'Publish changes'}
            </Button>
          </div>
        }
      />

      {loading ? (
        <Card><CardContent className="py-12 text-center text-sm text-slate-500">Loading…</CardContent></Card>
      ) : (
        <>
          {/* Financial Partners */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Banknote className="h-4 w-4 text-primary-600" />
                Financial Partners
                <span className="text-xs font-normal text-slate-500">({data.financialPartners.length})</span>
              </CardTitle>
              <Button size="sm" onClick={() => setFP([...data.financialPartners, EMPTY_FP()])}>
                <Plus className="h-4 w-4" /> Add
              </Button>
            </CardHeader>
            <CardContent className="space-y-3">
              {data.financialPartners.length === 0 && (
                <p className="py-6 text-center text-sm text-slate-500">No financial partners yet. Click <strong>Add</strong> to get started.</p>
              )}
              {data.financialPartners.map((fp, i) => (
                <FinancialRow
                  key={fp._id}
                  partner={fp}
                  index={i}
                  onChange={(idx, p) => setFP(data.financialPartners.map((x, j) => j === idx ? p : x))}
                  onDelete={(idx) => setFP(data.financialPartners.filter((_, j) => j !== idx))}
                />
              ))}
              {data.financialPartners.length > 0 && (
                <Button variant="outline" size="sm" className="w-full"
                  onClick={() => setFP([...data.financialPartners, EMPTY_FP()])}>
                  <Plus className="h-4 w-4" /> Add another
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Managing Partners */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Handshake className="h-4 w-4 text-primary-600" />
                Managing Partners
                <span className="text-xs font-normal text-slate-500">({data.managingPartners.length})</span>
              </CardTitle>
              <Button size="sm" onClick={() => setMP([...data.managingPartners, EMPTY_MP()])}>
                <Plus className="h-4 w-4" /> Add
              </Button>
            </CardHeader>
            <CardContent className="space-y-3">
              {data.managingPartners.length === 0 && (
                <p className="py-6 text-center text-sm text-slate-500">No managing partners yet. Click <strong>Add</strong> to get started.</p>
              )}
              {data.managingPartners.map((mp, i) => (
                <ManagingRow
                  key={mp._id}
                  partner={mp}
                  index={i}
                  onChange={(idx, p) => setMP(data.managingPartners.map((x, j) => j === idx ? p : x))}
                  onDelete={(idx) => setMP(data.managingPartners.filter((_, j) => j !== idx))}
                />
              ))}
              {data.managingPartners.length > 0 && (
                <Button variant="outline" size="sm" className="w-full"
                  onClick={() => setMP([...data.managingPartners, EMPTY_MP()])}>
                  <Plus className="h-4 w-4" /> Add another
                </Button>
              )}
            </CardContent>
          </Card>
        </>
      )}

      <p className="text-xs text-slate-400">
        Financial Partners appear as a numbered list on the left. Managing Partners appear as a table on the right. The section is hidden automatically if both groups are empty.
      </p>
    </div>
  );
}
